import { Injectable, inject } from '@angular/core';
import {
  HttpContext,
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpParams,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Observable, defer, throwError } from 'rxjs';
import { catchError, filter, finalize, map, shareReplay, switchMap, take, tap, throwIfEmpty, timeout } from 'rxjs/operators';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { environment } from '../../app/environment/environment';
import { ISession } from './interfaces/session.interface';
import { selectSelectedTenantId } from '../tenant-store/tenant.selectors';
import { SessionService } from './session.service';

const TENANT_HEADER = 'x-tenant-id';
/** A refresh that does not answer in this time is treated as a network failure: the waiting requests get an error. */
const REFRESH_TIMEOUT_MS = 30_000;

/**
 * Marks the request that is repeated after a successful refresh. If it gets a 401 again the cookies
 * are not the problem a refresh can fix (an old cookie of the same name shadowing the new one, a
 * session revoked in between): refreshing again would loop forever, so the session is closed.
 */
export const SESSION_REFRESHED = new HttpContextToken<boolean>(() => false);

@Injectable()
export class AuthHttpInterceptor implements HttpInterceptor {
  private session = inject(SessionService);
  private router = inject(Router);
  private store = inject(Store);

  public loginRoute = environment.loginUrl;
  public loginParam = 'login=true';
  public refreshRoute = environment.refreshRoute || `${environment.api}/auth/refreshToken`;
  isHandset = false;

  /**
   * The refresh in flight, shared by every request that gets a 401 meanwhile. It runs on its own
   * subscription, not on the one of the request that started it, so cancelling that request (a
   * switchMap, a destroyed component) neither aborts the refresh nor leaves the others waiting.
   */
  private refresh$: Observable<void> | null = null;

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      catchError((event: unknown) => {
        if (event instanceof HttpErrorResponse && event.status === 401) {
          if (this.session.isLoggedIn()) {
            if (req.context.get(SESSION_REFRESHED)) {
              // Repeated after a successful refresh and still refused: do not refresh again.
              this.endSession();
              return throwError(() => event);
            }
            return this.sharedRefresh(req, next).pipe(
              switchMap(() =>
                next.handle(this.repeated(req)).pipe(
                  catchError((error: unknown) => {
                    if (error instanceof HttpErrorResponse && error.status === 401) this.endSession();
                    return throwError(() => error);
                  })
                )
              )
            );
          } else {
            this.navigateLogin();
          }
        }
        return throwError(() => event);
      }),

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      map((res: any) => {
        if (
          res instanceof HttpResponse &&
          !!res.url &&
          res.url.indexOf(environment.api) >= 0 &&
          res?.body?.data
        ) {
          res = res.clone({ body: res.body.data });
        }

        return res;
      })
    );
  }

  /** The request again, marked so that a second 401 closes the session instead of refreshing. */
  private repeated(req: HttpRequest<unknown>): HttpRequest<unknown> {
    const context = new HttpContext();
    for (const key of req.context.keys()) {
      const token = key as HttpContextToken<unknown>;
      context.set(token, req.context.get(token));
    }
    return req.clone({ withCredentials: true, context: context.set(SESSION_REFRESHED, true) });
  }

  /**
   * The tenant the refresh is made in: the server answers 400 to a route without `x-tenant-id`, and the
   * session of the user is the one of the tenant he works in, so the selected tenant goes first. The
   * request that got the 401 may belong to another one (`public` for the tenant list, for instance, where
   * the user has no BASIC_ACCESS and the refresh would be refused), so its header is only the fallback.
   */
  private refreshTenant(triggering: HttpRequest<unknown>): string | null {
    let selected = '';
    this.store
      .select(selectSelectedTenantId)
      .pipe(take(1))
      .subscribe((tenantId) => (selected = tenantId == null ? '' : String(tenantId)));
    return selected || triggering.headers.get(TENANT_HEADER);
  }

  private sharedRefresh(triggering: HttpRequest<unknown>, next: HttpHandler): Observable<void> {
    if (!this.refresh$) {
      this.refresh$ = defer(() => {
        const tenantId = this.refreshTenant(triggering);
        const refReq = new HttpRequest<null>('POST', this.refreshRoute, null, {
          withCredentials: true,
          params: new HttpParams(),
          headers: tenantId ? new HttpHeaders({ [TENANT_HEADER]: tenantId }) : new HttpHeaders(),
        });
        return next.handle(refReq);
      }).pipe(
        filter((event): event is HttpResponse<unknown> => event instanceof HttpResponse),
        take(1),
        // A hung proxy (a deployment in progress) or a handler that ends without a response must reach the
        // waiting requests as an error like a network failure (status 0: no logout), not leave them waiting or
        // complete them empty.
        timeout({ first: REFRESH_TIMEOUT_MS, with: () => throwError(() => this.refreshFailure('The refresh timed out')) }),
        throwIfEmpty(() => this.refreshFailure('The refresh ended without a response')),
        tap((response) => {
          // The refresh returns the current session: replace the cached one so the menu and the
          // route guards follow a role/permission change without a new login. A server that
          // predates that answers `{ message }`, which must not overwrite the session.
          const refreshed = response.body as Partial<ISession> | null;
          if (refreshed?.user && Array.isArray(refreshed.permissions)) {
            this.session.refreshSession(refreshed as ISession);
          }
        }),
        map(() => undefined),
        catchError((error: unknown) => {
          // Only a refused refresh ends the session. Status 0 or 5xx (network down, a deployment in
          // progress) say nothing about the refresh token: keep the session and let the error through.
          if (error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403)) {
            this.endSession();
          }
          return throwError(() => error);
        }),
        finalize(() => (this.refresh$ = null)),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    }
    return this.refresh$;
  }

  private refreshFailure(statusText: string): HttpErrorResponse {
    return new HttpErrorResponse({ status: 0, statusText, url: this.refreshRoute });
  }

  private endSession(): void {
    this.session.logout();
    this.navigateLogin();
  }

  navigateLogin(): void {
    if (this.loginRoute.includes('http')) {
      window.location.href = this.loginRoute;
    } else {
      void this.router.navigate([this.loginRoute]);
    }
  }
}
